/**
 * Notes et évaluations de l'élève (migration 0321) — fonctions PURES.
 *
 * - Moyenne pondérée par les coefficients, chaque note ramenée sur 20
 *   (une note sur 5, 10, 100… compte à sa juste valeur).
 * - Répartition : à venir (pas encore notée, date ≥ aujourd'hui), à noter
 *   (date passée, pas de note), notées.
 * - SKILLCARDS à réviser pour une éval : celles qui y sont reliées, la plus
 *   fragile d'abord (maîtrise la plus basse, inconnue = à revoir en priorité).
 */

export const EVALUATION_MAX_SCORE_PRESETS = [5, 10, 20, 100] as const;
export const EVALUATION_MAX_CARDS = 30;

export interface EvaluationCardRef {
  card_id: string;
  card_title: string;
}

export interface StudentEvaluation {
  id: string;
  title: string;
  subject: string | null;
  /** Date de l'éval, `YYYY-MM-DD`. */
  eval_date: string;
  score: number | null;
  max_score: number;
  coefficient: number;
  note: string | null;
  graded_at: string | null;
  cards: EvaluationCardRef[];
}

/** Date locale `YYYY-MM-DD` (jamais l'UTC : un contrôle « demain » reste demain). */
export function localDateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** « 13,5 » / « 13.5 » / « 14 » → nombre ; vide ou invalide → null. */
export function parseGradeNumber(raw: string): number | null {
  const s = raw.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Note ramenée sur 20 (null si non notée). */
export function gradeOn20(e: Pick<StudentEvaluation, 'score' | 'max_score'>): number | null {
  if (e.score == null || e.max_score <= 0) return null;
  return (e.score / e.max_score) * 20;
}

/** Moyenne pondérée sur 20 des évals notées (null si aucune). */
export function weightedAverage(
  evals: readonly Pick<StudentEvaluation, 'score' | 'max_score' | 'coefficient'>[],
): number | null {
  let sum = 0;
  let weights = 0;
  for (const e of evals) {
    const g = gradeOn20(e);
    if (g == null) continue;
    sum += g * e.coefficient;
    weights += e.coefficient;
  }
  return weights > 0 ? sum / weights : null;
}

/** « 14,25 » (au plus 2 décimales, virgule) ; « — » si null. */
export function formatGradeNumber(n: number | null): string {
  if (n == null) return '—';
  return (Math.round(n * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

/** « 13,5 / 20 » ou « — / 20 ». */
export function formatScore(e: Pick<StudentEvaluation, 'score' | 'max_score'>): string {
  return `${formatGradeNumber(e.score)} / ${formatGradeNumber(e.max_score)}`;
}

/** Moyenne par matière (sans matière = « Autres »), triée par nom. */
export function averagesBySubject(
  evals: readonly StudentEvaluation[],
): { subject: string; average: number; count: number }[] {
  const groups = new Map<string, StudentEvaluation[]>();
  for (const e of evals) {
    if (e.score == null) continue;
    const key = e.subject?.trim() || 'Autres';
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups.entries()]
    .map(([subject, list]) => ({
      subject,
      average: weightedAverage(list) ?? 0,
      count: list.length,
    }))
    .sort((a, b) => a.subject.localeCompare(b.subject, 'fr'));
}

export interface EvaluationBuckets {
  /** Pas encore notées, aujourd'hui ou plus tard — la plus proche d'abord. */
  upcoming: StudentEvaluation[];
  /** Date passée, note pas encore saisie — la plus ancienne d'abord. */
  toGrade: StudentEvaluation[];
  /** Notées — la plus récente d'abord. */
  graded: StudentEvaluation[];
}

export function bucketEvaluations(
  evals: readonly StudentEvaluation[],
  today: string = localDateKey(),
): EvaluationBuckets {
  const upcoming: StudentEvaluation[] = [];
  const toGrade: StudentEvaluation[] = [];
  const graded: StudentEvaluation[] = [];
  for (const e of evals) {
    if (e.score != null) graded.push(e);
    else if (e.eval_date >= today) upcoming.push(e);
    else toGrade.push(e);
  }
  upcoming.sort((a, b) => a.eval_date.localeCompare(b.eval_date));
  toGrade.sort((a, b) => a.eval_date.localeCompare(b.eval_date));
  graded.sort((a, b) => b.eval_date.localeCompare(a.eval_date));
  return { upcoming, toGrade, graded };
}

/** Jours entre aujourd'hui et la date de l'éval (0 = aujourd'hui, < 0 = passée). */
export function daysUntil(evalDate: string, today: string = localDateKey()): number {
  const toUtc = (k: string) => {
    const [y, m, d] = k.split('-').map(Number);
    return Date.UTC(y!, (m ?? 1) - 1, d ?? 1);
  };
  return Math.round((toUtc(evalDate) - toUtc(today)) / 86_400_000);
}

/** « aujourd'hui », « demain », « dans 3 jours », « il y a 2 jours ». */
export function countdownLabel(evalDate: string, today: string = localDateKey()): string {
  const n = daysUntil(evalDate, today);
  if (n === 0) return 'aujourd’hui';
  if (n === 1) return 'demain';
  if (n === -1) return 'hier';
  if (n > 1) return `dans ${n} jours`;
  return `il y a ${-n} jours`;
}

/** « mardi 14 octobre » (date locale). */
export function evaluationDateLabel(evalDate: string): string {
  const [y, m, d] = evalDate.split('-').map(Number);
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(y!, (m ?? 1) - 1, d ?? 1));
}

/**
 * SKILLCARDS d'une éval à réviser, la plus fragile d'abord : maîtrise connue
 * la plus basse ; une carte jamais travaillée (maîtrise inconnue) passe avant.
 */
export function cardsToReview<T extends EvaluationCardRef>(
  cards: readonly T[],
  masteryById: ReadonlyMap<string, number>,
): (T & { mastery: number | null })[] {
  return cards
    .map((c) => ({ ...c, mastery: masteryById.get(c.card_id) ?? null }))
    .sort((a, b) => (a.mastery ?? -1) - (b.mastery ?? -1));
}

/** Ligne PostgREST (numeric en chaîne possible) → évaluation typée. */
export function normalizeEvaluation(row: Record<string, unknown>): StudentEvaluation {
  const num = (v: unknown, fallback: number) => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const rawCards = Array.isArray(row.student_evaluation_cards) ? row.student_evaluation_cards : [];
  return {
    id: String(row.id),
    title: String(row.title ?? ''),
    subject: typeof row.subject === 'string' && row.subject ? row.subject : null,
    eval_date: String(row.eval_date ?? ''),
    score: row.score == null ? null : num(row.score, 0),
    max_score: num(row.max_score, 20),
    coefficient: num(row.coefficient, 1),
    note: typeof row.note === 'string' && row.note ? row.note : null,
    graded_at: typeof row.graded_at === 'string' ? row.graded_at : null,
    cards: (rawCards as Record<string, unknown>[]).map((c) => ({
      card_id: String(c.card_id),
      card_title: String(c.card_title ?? ''),
    })),
  };
}
